// The VIRTUALYARD provider's pure, shared rules (docs/lead-relay.md §4, §5).
// No I/O, no clock. Moved here from compose.ts / routeRules.ts / routeForm.ts on
// 2026-10-05, when the relay grew a provider seam: everything in this file is a
// fact about VirtualYard's iframe form (or the BYD form behind it), not about
// the relay. The server half (fetch, parse, multipart POST) is
// server/modules/leadRelay/virtualyard.ts + virtualyardAttempt.ts.
//
// Every guard in this file is mutation-checked in compose.test.ts /
// routeRules.test.ts — deleting the blocklist, the required-field check or the
// opt-in rule fails a named test.

import type {
  LeadInput,
  LeadInterestFallback,
  VirtualYardRouteConfig,
  VirtualYardSentFields,
} from '../../types/leadRelay.js';
import { isBlockedInterest } from './blocklist.js';
import type { ComposeOptions, ComposeResult } from './compose.js';
import { effectiveModelMap, type ModelMapOverrides } from './modelMapStandard.js';
import { normalizeKey } from './normalize.js';
import type { RouteProblem } from './routeRules.js';

/** The only host the virtualyard provider may submit to. */
export const VIRTUALYARD_SUBMIT_HOST = 'dealers.virtualyard.com.au';
export const VIRTUALYARD_SUBMIT_URL = `https://${VIRTUALYARD_SUBMIT_HOST}/api/v2/post.php`;

/**
 * Every `interestedin` option on the EV Dealer Group BYD Enquire Now form,
 * read from the live page 2026-10-02 (the same list formPage.test.ts pins
 * against the recorded fixture). ⚠️ It includes the TWO BLOCKED options — they
 * are on the form; a route may never allow them (§5.2).
 */
export const BYD_INTEREST_OPTIONS: readonly string[] = [
  'General Enquiry',
  'Finance Enquiry',
  'Novated Lease Enquiry',
  'BYD ATTO 1',
  'BYD ATTO 2',
  'BYD ATTO 3',
  'BYD DOLPHIN',
  'BYD SEAL',
  'BYD SEALION 5',
  'BYD SEALION 6',
  'BYD SEALION 7',
  'BYD SEALION 8',
  'BYD SHARK 6',
];

/** The options a route may send: the form's options minus the blocked two. */
export const SENDABLE_INTEREST_OPTIONS: readonly string[] = BYD_INTEREST_OPTIONS.filter(
  (o) => !isBlockedInterest(o),
);

export const TEMPLATE_PLACEHOLDERS: readonly string[] = ['campaign_name', 'form_name', 'model', 'extra'];

/** VirtualYard's editable route fields, as the CRM form supplies them. */
export interface VirtualYardRouteFields {
  formUrl: string;
  submitUrlBase: string;
  lid: string;
  defaultInterest: string;
  allowedInterests: string[];
  /** The shared standard it inherits from; null = none (§25). */
  modelMapStandardId: string | null;
  /** ⭐ Only the departures from the standard; `null` removes a standard row. */
  modelMapOverrides: ModelMapOverrides;
  messageTemplate: string;
}

const LID = /^\d+:\d+$/;
const POSTCODE = /^\d{4}$/;

// ── route-save rules (§5.2, §5.3, §9.1) ──────────────────────────────────────

/**
 * The last six characters of the form URL's `token` — what the audit log
 * records instead of the URL (§9.3). Enough to debug and to see two routes
 * sharing a token; not enough to submit with. null when there is no token.
 */
export function formUrlTokenTail(formUrl: string): string | null {
  try {
    const token = new URL(formUrl).searchParams.get('token');
    return token && token.length >= 6 ? token.slice(-6) : null;
  } catch {
    return null;
  }
}

export function templateProblems(template: string): string[] {
  const problems: string[] = [];
  if (!template.trim().startsWith(MESSAGE_MARKER)) {
    problems.push(`must start with "${MESSAGE_MARKER}" — the attribution marker leads`);
  }
  for (const match of template.matchAll(/\{([^{}]*)\}/g)) {
    const name = match[1] ?? '';
    if (!TEMPLATE_PLACEHOLDERS.includes(name)) {
      problems.push(`unknown placeholder {${name}} would reach the dealer literally`);
    }
  }
  return problems;
}

/**
 * The VirtualYard half of validateLeadRoute. None quotes formUrl (it carries the token).
 *
 * `standards` — the shared model maps by id. When supplied (the server and the
 * edit form both supply it), the EFFECTIVE map — the standard plus this route's
 * overrides — is checked, and an unknown standard id is refused. When absent,
 * only the overrides themselves are checked.
 */
export function virtualYardRouteProblems(
  fields: VirtualYardRouteFields,
  standards?: ReadonlyMap<string, Record<string, string>>,
): RouteProblem[] {
  const problems: RouteProblem[] = [];
  const add = (field: string, message: string) => problems.push({ field, message });

  if (!LID.test(fields.lid.trim())) add('lid', 'must be the store id in the form id:lid, e.g. 112:2820');

  let formUrl: URL | null = null;
  try {
    formUrl = new URL(fields.formUrl);
  } catch {
    add('formUrl', 'is not a valid URL');
  }
  if (formUrl) {
    if (formUrl.protocol !== 'https:') add('formUrl', 'must be https');
    if (!formUrl.searchParams.get('token')) add('formUrl', 'has no token parameter — paste the full form URL');
    if (formUrl.pathname.split('/').filter(Boolean).length === 0) add('formUrl', 'has no form key in its path');
  }
  if (fields.submitUrlBase !== VIRTUALYARD_SUBMIT_URL) {
    add('submitUrlBase', `must be ${VIRTUALYARD_SUBMIT_URL}`);
  }

  // ⚠️ The blocklist at save time (§5.2) — one of its three enforcement points.
  if (isBlockedInterest(fields.defaultInterest)) {
    add('defaultInterest', `"${fields.defaultInterest}" is blocked — it sends the customer's details to a third party they have not agreed to`);
  } else if (!fields.allowedInterests.includes(fields.defaultInterest)) {
    add('defaultInterest', 'must be one of the allowed interests');
  }
  for (const value of fields.allowedInterests) {
    if (isBlockedInterest(value)) add('allowedInterests', `"${value}" is blocked and cannot be allowed`);
  }
  // An invariant, not an input rule: keys are NORMALISED at the save boundary
  // (routeService check(), fieldsFromForm), so this only fires for a caller
  // that skipped it — comparison downstream is a plain equality on these keys.
  for (const answer of Object.keys(fields.modelMapOverrides)) {
    if (normalizeKey(answer) !== answer) add('modelMap', `key "${answer}" must be normalised (lowercase, single spaces)`);
  }
  let standard: Record<string, string> | null = null;
  if (fields.modelMapStandardId !== null && standards) {
    standard = standards.get(fields.modelMapStandardId) ?? null;
    if (standard === null) add('modelMap', `uses the shared model map "${fields.modelMapStandardId}", which does not exist`);
  }
  // The rows that will actually be sent from: the effective map when the
  // standard is known, otherwise just the rows this route itself sets.
  const rows: Record<string, string> = standards
    ? effectiveModelMap(standard, fields.modelMapOverrides)
    : Object.fromEntries(
        Object.entries(fields.modelMapOverrides).filter((e): e is [string, string] => e[1] !== null),
      );
  for (const [answer, value] of Object.entries(rows)) {
    if (isBlockedInterest(value)) {
      add('modelMap', `"${answer}" maps to blocked "${value}"`);
    } else if (!fields.allowedInterests.includes(value)) {
      add('modelMap', `"${answer}" maps to "${value}", which is not an allowed interest`);
    }
  }

  for (const message of templateProblems(fields.messageTemplate)) add('messageTemplate', message);
  return problems;
}

function formHost(formUrl: string): string | null {
  try {
    return new URL(formUrl).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/**
 * ⚠️ A WARNING, NOT A REFUSAL. The group-wide form token is copied onto every
 * route for that dealer, so a token rotation is an N-document edit — and missing
 * one fails THAT store silently (its form fetch keeps working, the submit is
 * rejected) while every other store goes on working. So when a route is saved,
 * flag any other ACTIVE route on the same form host whose token tail differs:
 * either this edit is the rotation and the others still need it, or this route
 * was saved with a stale token.
 *
 * Compared by the token TAIL (§9.3), the same six characters the audit log and
 * the Lead Routes list show, so a person can match the warning to the column.
 */
export function sharedCredentialWarnings(
  routeId: string,
  formUrl: string,
  others: ReadonlyArray<{ id: string; name: string; active: boolean; formUrl: string }>,
): string[] {
  const host = formHost(formUrl);
  if (host === null) return [];
  const tail = formUrlTokenTail(formUrl);
  const differing = others.filter(
    (other) =>
      other.id !== routeId &&
      other.active &&
      formHost(other.formUrl) === host &&
      formUrlTokenTail(other.formUrl) !== tail,
  );
  if (differing.length === 0) return [];
  const listed = differing
    .map((other) => `${other.name} (token …${formUrlTokenTail(other.formUrl) ?? 'none'})`)
    .join(', ');
  return [
    `This route's form token ends …${tail ?? 'none'}, but ${differing.length === 1 ? 'another active route' : `${differing.length} other active routes`} on ${host} ${differing.length === 1 ? 'uses' : 'use'} a different one: ${listed}. ` +
      'If the dealer rotated the token, update every route on this form — a route left on the old token fails silently while the others keep working.',
  ];
}

// ── interestedin (§5.1) ──────────────────────────────────────────────────────

export interface InterestChoice {
  value: string;
  fallback: LeadInterestFallback | null;
}

/**
 * Map the lead's model answer to an exact form option. Anything that does not
 * map to an allowed, unblocked value falls back to the route default — the raw
 * answer still appears in the message, so nothing the lead said is lost.
 */
export function resolveInterest(
  config: Pick<VirtualYardRouteConfig, 'defaultInterest' | 'modelMap' | 'allowedInterests'>,
  model: string | undefined,
): InterestChoice {
  const key = normalizeKey(model);
  if (key === '') return { value: config.defaultInterest, fallback: 'none' };
  const mapped = config.modelMap[key];
  if (mapped === undefined) return { value: config.defaultInterest, fallback: 'unmapped' };
  // ⚠️ Blocked is checked BEFORE allowed: a blocked value sitting in
  // allowedInterests (a hand-edited route) must still never be sent.
  if (isBlockedInterest(mapped)) return { value: config.defaultInterest, fallback: 'blocked' };
  if (!config.allowedInterests.includes(mapped)) {
    return { value: config.defaultInterest, fallback: 'not_allowed' };
  }
  return { value: mapped, fallback: null };
}

// ── optin (§5.4) ─────────────────────────────────────────────────────────────

/**
 * `yes` ONLY from an explicit affirmative answer. Absent, blank, unknown or any
 * free-text variant is `no`. Note the dealer's own form defaults its radio to
 * yes — that default must never reach a Meta lead.
 */
export function resolveOptin(answer: string | undefined): 'yes' | 'no' {
  const value = normalizeKey(answer);
  return value === 'yes' || value === 'true' ? 'yes' : 'no';
}

// ── message (§5.3) ───────────────────────────────────────────────────────────

export const MESSAGE_MARKER = 'Facebook Lead';

export const DEFAULT_MESSAGE_TEMPLATE =
  'Facebook Lead — {campaign_name}\nForm: {form_name}\nModel: {model}\n{extra}';

function renderExtra(extra: Record<string, string> | undefined): string {
  if (!extra) return '';
  return Object.entries(extra)
    .filter(([question, answer]) => question.trim() !== '' && answer.trim() !== '')
    .map(([question, answer]) => `${question.trim()}: ${answer.trim()}`)
    .join('\n');
}

/**
 * Render the route template. Unknown placeholders stay literal (the route-save
 * check refuses them, §5.3). ⚠️ The attribution marker LEADS: if a template
 * somehow does not start with it, the marker line is prepended rather than
 * trusting the save-time check alone.
 */
export function renderMessage(template: string, input: LeadInput): string {
  const values: Record<string, string> = {
    campaign_name: (input.campaign_name ?? '').trim(),
    form_name: (input.form_name ?? '').trim(),
    model: (input.model ?? '').trim(),
    extra: renderExtra(input.extra),
  };
  const body = template
    .replace(/\{(campaign_name|form_name|model|extra)\}/g, (_m, name: string) => values[name] ?? '')
    .replace(/[ \t]+$/gm, '')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  if (body.startsWith(MESSAGE_MARKER)) return body;
  const marker = `${MESSAGE_MARKER} — ${values.campaign_name}`.trim();
  return body === '' ? marker : `${marker}\n${body}`;
}

// ── required fields (§4.5) ───────────────────────────────────────────────────

/**
 * Required by VirtualYard: email, postcode, lid, message. ⚠️ Names are NOT
 * checked here (2026-10-07): an empty name goes out as NAME_PLACEHOLDER at the
 * transport (names.ts, a blanket rule for every provider), so it can no longer
 * fail a lead.
 */
export function missingRequiredFields(sent: VirtualYardSentFields): string[] {
  const missing: string[] = [];
  if (sent.email === '') missing.push('email');
  if (!POSTCODE.test(sent.postcode)) missing.push('postcode');
  if (!LID.test(sent.lid)) missing.push('lid');
  if (sent.message === '') missing.push('message');
  return missing;
}

// ── compose ──────────────────────────────────────────────────────────────────

export function composeVirtualYardLead(
  config: VirtualYardRouteConfig,
  input: LeadInput,
  options: ComposeOptions = {},
): ComposeResult {
  // A hand-edited route must not be able to route around the save-time check.
  if (isBlockedInterest(config.defaultInterest)) {
    return {
      ok: false,
      reason: 'blocked_default_interest',
      detail: `Route default "${config.defaultInterest}" is blocked.`,
    };
  }
  const interest = resolveInterest(config, input.model);
  const sent: VirtualYardSentFields = {
    firstName: (input.first_name ?? '').trim(),
    lastName: (input.last_name ?? '').trim(),
    email: (input.email ?? '').trim(),
    phone: (input.phone ?? '').trim(),
    // The lead's OWN postcode, unmodified apart from trimming (§2.2).
    postcode: (input.postcode ?? '').trim(),
    lid: config.fixed.lid.trim(),
    interestedin: interest.value,
    employer: '',
    message: (options.messageOverride ?? renderMessage(config.messageTemplate, input)).trim(),
    optin: resolveOptin(input.optin),
  };
  const missing = missingRequiredFields(sent);
  if (missing.length > 0) {
    return { ok: false, reason: 'missing_required', missing, sent, interestFallback: interest.fallback };
  }
  return { ok: true, sent, interestFallback: interest.fallback };
}

/** A resent TEST lead keeps the marked message it was first composed with. */
export function virtualYardTestMessageOverride(sent: VirtualYardSentFields | null): string | undefined {
  return sent?.message ?? undefined;
}
